import { detailsSlots } from '@/shared/details'
import { shellSlots, type AdminHomeLink, type FeatureRoute, type SlotContribution } from '@/shared/registry'

import { userDetailsSlot, userFormTabsSlot } from '../users/user'
import {
  blockoutRoute,
  blockoutsPath,
  createBlockoutPath,
  myBlockoutsPath,
  readBlockoutsPermission,
  readUsersPermission,
  writeBlockoutsPermission,
} from './blockout'
import { instructorsPath, myCompetenciesPath, readInstructorDetailsPermission } from './instructor'

export { myBlockoutsPath } from './blockout'
export { myCompetenciesPath } from './instructor'

/**
 * My Competencies (SM-30) and My Blockout Times, which the instructor dashboard links to, Find
 * and Create Instructor Blockout Time (SM-36), and Find Instructors (SM-31).
 */
export const instructorRoutes: readonly FeatureRoute[] = [
  {
    path: instructorsPath,
    titleId: 'siteManagement.instructors.find.title',
    page: () => import('./FindInstructorsPage').then((module) => module.FindInstructorsPage),
  },
  {
    path: myCompetenciesPath,
    titleId: 'siteManagement.instructors.my.title',
    page: () => import('./MyCompetenciesPage').then((module) => module.MyCompetenciesPage),
  },
  {
    path: myBlockoutsPath,
    titleId: 'siteManagement.instructors.blockouts.my.title',
    page: () => import('./MyBlockoutTimesPage').then((module) => module.MyBlockoutTimesPage),
  },
  {
    path: blockoutsPath,
    titleId: 'siteManagement.instructors.blockouts.find.title',
    page: () => import('./FindBlockoutsPage').then((module) => module.FindBlockoutsPage),
  },
  {
    path: createBlockoutPath,
    titleId: 'siteManagement.instructors.blockouts.create.title',
    page: () => import('./CreateBlockoutPage').then((module) => module.CreateBlockoutPage),
  },
  {
    path: blockoutRoute,
    titleId: 'siteManagement.instructors.blockouts.details.title',
    page: () => import('./BlockoutDetailsPage').then((module) => module.BlockoutDetailsPage),
  },
]

/** Find Instructors, and Find and Create Instructor Blockout Time, on Admin Home under Instructors. */
export const instructorAdminHomeLinks: readonly AdminHomeLink[] = [
  { id: 'find-instructors', category: 'instructors', labelId: 'siteManagement.instructors.find.title', path: instructorsPath, permission: readUsersPermission },
  { id: 'find-instructor-blockouts', category: 'instructors', labelId: 'siteManagement.instructors.blockouts.find.title', path: blockoutsPath, permission: readBlockoutsPermission },
  { id: 'create-instructor-blockout', category: 'instructors', labelId: 'siteManagement.instructors.blockouts.create.title', path: createBlockoutPath, permission: writeBlockoutsPermission },
]

/**
 * The Instructor tab on User Details, and on Create and Edit User, saved with the form (SM-30), the
 * Blockout Times tab on User Details, which has its own permission (SM-36), and the contract
 * reconfirmation prompt after sign-in (SM-37).
 */
export const instructorSlots: readonly SlotContribution[] = [
  {
    // Asks a contract instructor whose consent is due whether they stay one.
    kind: 'panel',
    slot: shellSlots.gates,
    id: 'contract-consent',
    component: () => import('./ContractConsentGate').then((module) => module.ContractConsentGate),
  },
  {
    kind: 'tab',
    slot: detailsSlots(userDetailsSlot).tabs,
    id: 'instructor',
    labelId: 'siteManagement.instructors.tab',
    permission: readInstructorDetailsPermission,
    component: () => import('./InstructorDetailsTab').then((module) => module.InstructorDetailsTab),
  },
  {
    kind: 'tab',
    slot: userFormTabsSlot,
    id: 'instructor',
    labelId: 'siteManagement.instructors.tab',
    permission: readInstructorDetailsPermission,
    component: () => import('./InstructorFormTab').then((module) => module.InstructorFormTab),
  },
  {
    kind: 'tab',
    slot: detailsSlots(userDetailsSlot).tabs,
    id: 'blockout-times',
    labelId: 'siteManagement.instructors.blockouts.tab',
    permission: readBlockoutsPermission,
    isPersonRecord: true,
    component: () => import('./BlockoutTimesTab').then((module) => module.BlockoutTimesTab),
  },
]
